import { syncTomSelectFromNative } from './site-modal-selects';

/**
 * @return {Array<{id: number, title: string, service_ids: number[]}>}
 */
const getAppointmentSpecialistCatalog = () => {
	const catalog = window.lecardoVeinModals?.appointmentCatalog?.specialists;

	return Array.isArray(catalog) ? catalog : [];
};

/**
 * @param {HTMLSelectElement} select
 * @return {string}
 */
const getSpecialistPlaceholderLabel = (select) => {
	if (select.dataset.appointmentPlaceholder) {
		return select.dataset.appointmentPlaceholder;
	}

	const blankOption = select.querySelector('option[value=""]');
	const label = blankOption?.textContent?.trim() || '';

	select.dataset.appointmentPlaceholder = label;

	return label;
};

/**
 * @param {HTMLSelectElement} select
 * @param {string} serviceId
 * @return {void}
 */
const rebuildSpecialistOptionsForService = (select, serviceId) => {
	const normalizedServiceId = String(serviceId || '').trim();
	const catalog = getAppointmentSpecialistCatalog();
	const placeholder = getSpecialistPlaceholderLabel(select);

	let specialists = catalog;

	if (normalizedServiceId && catalog.length) {
		const serviceIdNumber = Number(normalizedServiceId);

		specialists = catalog.filter((specialist) => {
			const serviceIds = Array.isArray(specialist.service_ids)
				? specialist.service_ids
				: [];

			return serviceIds.includes(serviceIdNumber);
		});
	}

	select.replaceChildren();

	if (placeholder) {
		const placeholderOption = document.createElement('option');

		placeholderOption.value = '';
		placeholderOption.textContent = placeholder;
		select.appendChild(placeholderOption);
	}

	specialists.forEach((specialist) => {
		const option = document.createElement('option');

		option.value = String(specialist.id);
		option.textContent = specialist.title;
		select.appendChild(option);
	});
};

/**
 * @param {HTMLSelectElement} select
 * @param {string} preferredId
 * @return {void}
 */
const applyNativeSelectValue = (select, preferredId) => {
	const hasOption = Array.from(select.options).some(
		(option) => option.value === preferredId
	);

	if (preferredId && hasOption) {
		select.value = preferredId;
	} else {
		select.value = '';
	}

	syncTomSelectFromNative(select);
};

/**
 * @param {HTMLSelectElement} select
 * @return {string}
 */
const getSingleSpecialistOptionValue = (select) => {
	const options = Array.from(select.options).filter(
		(option) => '' !== option.value
	);

	return 1 === options.length ? options[0].value : '';
};

/**
 * Prefills appointment CF7 catalog selects (specialist / service).
 *
 * @param {HTMLElement|null|undefined} modal Appointment modal root.
 * @param {{ specialistId?: string|null, serviceId?: string|null }|undefined} detail Open context.
 * @return {void}
 */
export function applyAppointmentCatalogSelects(modal, detail = {}) {
	if (!modal || 'appointment' !== modal.getAttribute('data-modal')) {
		return;
	}

	const specialistId = String(
		detail.specialistId ?? modal.dataset.specialistId ?? ''
	).trim();
	const serviceId = String(
		detail.serviceId ?? modal.dataset.serviceId ?? ''
	).trim();

	const specialistSelect = modal.querySelector('select[name="specialist"]');
	const serviceSelect = modal.querySelector('select[name="service"]');

	if (serviceSelect instanceof HTMLSelectElement) {
		applyNativeSelectValue(serviceSelect, serviceId);
	}

	const activeServiceId =
		serviceSelect instanceof HTMLSelectElement && serviceSelect.value
			? serviceSelect.value
			: serviceId;

	if (specialistSelect instanceof HTMLSelectElement) {
		rebuildSpecialistOptionsForService(specialistSelect, activeServiceId);

		let specialistToApply = specialistId;

		if (!specialistToApply) {
			specialistToApply = getSingleSpecialistOptionValue(specialistSelect);
		}

		applyNativeSelectValue(specialistSelect, specialistToApply);
	}
}

/**
 * Binds service → specialist filtering when the user changes the service field.
 *
 * @param {HTMLElement|null|undefined} modal Appointment modal root.
 * @return {void}
 */
export function bindAppointmentCatalogSelectSync(modal) {
	if (!modal || 'appointment' !== modal.getAttribute('data-modal')) {
		return;
	}

	if ('true' === modal.dataset.appointmentCatalogSyncBound) {
		return;
	}

	const serviceSelect = modal.querySelector('select[name="service"]');

	if (!(serviceSelect instanceof HTMLSelectElement)) {
		return;
	}

	modal.dataset.appointmentCatalogSyncBound = 'true';

	serviceSelect.addEventListener('change', () => {
		applyAppointmentCatalogSelects(modal, {
			serviceId: serviceSelect.value,
			specialistId: null,
		});
	});
}
